import { Controller } from "@hotwired/stimulus";
import { logError } from "../../../../Libraries/CoreWeb/CoreWeb/Features/Shared/logging";
import { webTranslations } from "../webTranslations";
import { normalizeContentShareUrl, shareContentLink } from "./ContentShareUi";

export default class ContentShareController extends Controller<HTMLElement> {
    static targets = ["tooltip"];

    declare readonly hasTooltipTarget: boolean;
    declare readonly tooltipTarget: HTMLElement;

    private hideTooltipTimer?: number;

    async share(event: Event): Promise<void> {
        event.preventDefault();
        const button = event.currentTarget as HTMLElement | null;
        const rawUrl = button?.dataset.shareUrl;
        if (!rawUrl) {
            return;
        }

        const url = normalizeContentShareUrl(rawUrl, window.location.origin);
        const result = await shareContentLink(button.dataset.shareTitle ?? document.title, url, {
            share: typeof navigator.share === "function" ? navigator.share.bind(navigator) : undefined,
            writeText: typeof navigator.clipboard?.writeText === "function"
                ? navigator.clipboard.writeText.bind(navigator.clipboard)
                : undefined,
            prompt: window.prompt.bind(window),
            report: (error, stage) => {
                void logError(error, { component: "content-share", stage }).catch(() => undefined);
            }
        });

        if (result === "clipboard") {
            this.showClipboardTooltip();
        }
    }

    disconnect(): void {
        this.clearTooltip();
    }

    private showClipboardTooltip(): void {
        if (!this.hasTooltipTarget) {
            return;
        }

        if (this.hideTooltipTimer !== undefined) {
            window.clearTimeout(this.hideTooltipTimer);
        }

        this.tooltipTarget.textContent = webTranslations.linkCopiedToClipboard;
        this.tooltipTarget.hidden = false;
        this.hideTooltipTimer = window.setTimeout(() => this.clearTooltip(), 2_000);
    }

    private clearTooltip(): void {
        if (this.hideTooltipTimer !== undefined) {
            window.clearTimeout(this.hideTooltipTimer);
            this.hideTooltipTimer = undefined;
        }

        if (this.hasTooltipTarget) {
            this.tooltipTarget.hidden = true;
            this.tooltipTarget.textContent = "";
        }
    }
}
